/**
 * Truck API
 *
 * API endpoints for truck management operations
 */

import toast from "react-hot-toast";

import { nextCargoApi } from "../../baseApi";
import { companyApi } from "../company/company.api";
import { orderApi } from "../order/order.api";
import type { CreateTruckArgs, ListTrucks, RebindTruckArgs, SaveNewTruck, SearchTruck, Truck, TrucksFilters } from "./truck.types";

/**
 * Truck API endpoints
 * Provides CRUD operations for truck management
 */
export const truckApi = nextCargoApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Get all trucks
     * @returns Array of trucks
     */
    getTrucks: build.query<Truck[], void, Truck[]>({
      query: () => "/truck",
      transformResponse: (response) => {
        const emptyMessage = "Nenhuma";
        const formatEmptyFields = response.map((item) => ({
          ...item,
          shippingCompany: { ...item.shippingCompany, name: item.shippingCompany?.name || emptyMessage },
          company: { ...item.company, name: item.company?.name || emptyMessage }
        }));

        return formatEmptyFields;
      },
      onQueryStarted: async (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando caminhões...",
          success: "Caminhões carregados com sucesso!",
          error: "Erro ao carregar caminhões."
        });
      }
    }),

    /**
     * Get a single truck by ID
     * @param id - Truck ID
     * @returns Full truck details
     */
    getTruck: build.query<Truck, Pick<Truck, "id">, Truck>({
      query: ({ id }) => `/truck/${id}`,
      keepUnusedDataFor: 0,
      // transformResponse: (response) => {
      //   const normalizedRes = structuredClone(response);
      //   normalizedRes.driver.name = normalizedRes.driver.name.replace(/\s+/g, " ").trim();

      //   return normalizedRes;
      // },
      onQueryStarted: async (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando caminhão...",
          success: "Caminhão carregado com sucesso!"
          // error: "Erro ao carregar caminhão."
        });
      }
    }),

    /**
     * Check if a truck exists by license plate
     * @param licensePlate - License plate to check
     * @returns Boolean indicating if truck exists
     */
    getIsTruck: build.query<{ isTruck: boolean }, Pick<Truck, "licensePlate">>({
      query: ({ licensePlate }) => `/truck/isTruck/${licensePlate}`,
      onQueryStarted: async ({ licensePlate }, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: `Verificando placa ${licensePlate}...`,
          success: "Verificação concluída!",
          error: (err) => {
            console.log(err);

            return "Erro ao verificar caminhão.";
          }
        });
      }
    }),

    /**
     * Search trucks by license plate
     * @param licensePlate - License plate to search
     * @returns Array of matching trucks (minimal fields)
     */
    searchTrucks: build.query<SearchTruck[], Pick<SearchTruck, "licensePlate">>({
      query: ({ licensePlate }) => {
        const formattedPlate = licensePlate.trim().replace(/.*- /, "").replace(/\s+/g, "");

        return {
          url: `/truck/like/${formattedPlate}`
        };
      }
      // onQueryStarted: async (args, { queryFulfilled }) => {
      //   toast.promise(queryFulfilled, {
      //     loading: "Buscando caminhões...",
      //     success: "Caminhões buscados com sucesso!",
      //     error: "Erro ao buscar caminhões."
      //   });
      // }
    }),

    /**
     * Infinite/paginated query for trucks.
     *
     * @remarks
     * This endpoint provides server-side pagination for a list of trucks.
     * It supports additional filter parameters and can be used for infinite scroll implementations.
     *
     * @typeParam ListTrucks - The paginated trucks list response (including count, page, etc).
     * @typeParam { id, shippingCompanyId, params } - Query object for initial filters and params.
     * @typeParam App.Filters.Pagination<Truck> - Pagination params sent to backend.
     *
     * @returns ListTrucks object with truck data and pagination info.
     *
     */
    listTrucks: build.infiniteQuery<ListTrucks, { id: number; shippingCompanyId?: number; params?: App.Filters.Base<Truck> }, App.Filters.Pagination<Truck>, ListTrucks>({
      infiniteQueryOptions: {
        initialPageParam: { page: 0, pageSize: 10, sortDirection: "desc", sortField: "createdAt" },
        getNextPageParam: (lastPage, _, lastPageParam) => {
          const nextPage = lastPageParam.page + 1;
          const remainingPages = lastPage?.total - nextPage;

          if (remainingPages <= 0) {
            return undefined;
          }

          return {
            ...lastPageParam,
            page: nextPage
          };
        },
        getPreviousPageParam: (_, __, firstPageParam) => {
          const prevPage = firstPageParam.page - 1;
          if (prevPage < 0) return undefined;

          return {
            ...firstPageParam,
            page: prevPage
          };
        }
      },
      query: ({ queryArg: { params }, pageParam }) => ({
        url: "/truck",
        params: Object.assign({}, pageParam, params)
      }),
      onQueryStarted: async (args, { queryFulfilled, dispatch }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando caminhões...",
          success: "Caminhões carregados com sucesso!",
          error: "Erro ao carregar caminhões."
        });

        const { data } = await queryFulfilled;
        dispatch(
          truckApi.util.upsertQueryEntries(
            data.pages
              .flatMap((page) => page.data)
              .map((item) => ({
                endpointName: "getTruck",
                arg: { id: item.id },
                value: item
              }))
          )
        );
      }
    }),

    /**
     * Create or update a truck
     * @param payload - Truck data (id optional for create, required for update)
     * @returns Created/updated truck
     */
    createTruck: build.mutation<Truck, CreateTruckArgs>({
      query: (args) => {
        const { id = null, vehicleModel, company, driver, shippingCompany, brand: _brand, isEnabled, ...rest } = args;
        const toNumber = (val?: string | number | null) => {
          if (val === null || val === undefined) return null;
          return typeof val === "string" ? Number(val) || null : val;
        };

        const body = {
          ...rest,
          id: Number(id),
          vehicleModel: vehicleModel?.id ? { id: toNumber(vehicleModel.id) } : null,
          company: company?.id ? { id: toNumber(company.id) } : null,
          driver: driver?.id ? { id: toNumber(driver.id) } : null,
          shippingCompany: shippingCompany?.id ? { id: toNumber(shippingCompany.id) } : null,
          isEnabled,
          status: isEnabled
        };

        return {
          url: `/truck/save`,
          method: "POST",
          body
        };
      },
      onQueryStarted: async (args, { queryFulfilled }) => {
        const isEdit = Boolean(args.id);
        toast.promise(queryFulfilled, {
          loading: isEdit ? "Atualizando caminhão..." : "Criando caminhão...",
          success: isEdit ? "Caminhão atualizado com sucesso!" : "Caminhão criado com sucesso!",
          error: isEdit ? "Erro ao atualizar caminhão." : "Erro ao criar caminhão."
        });
      }
    }),
    saveNewTruck: build.mutation<SaveNewTruck["result"], SaveNewTruck["args"]>({
      query: (args) => {
        const { id = null, vehicleModel, company, driver, shippingCompany, isEnabled, ...rest } = args;

        const toNumber = (val?: string | number | null) => {
          if (val === null || val === undefined) return null;
          return typeof val === "string" ? Number(val) || null : val;
        };

        const body = {
          ...rest,
          id: id ? toNumber(id) : null,
          vehicleModel: vehicleModel?.id
            ? {
                id: toNumber(vehicleModel.id),
                truckBrand: vehicleModel.truckBrand?.id ? { id: toNumber(vehicleModel.truckBrand.id) } : undefined
              }
            : null,
          company: company?.id ? { id: toNumber(company.id) } : null,
          driver: driver?.id ? { id: toNumber(driver.id) } : null,
          shippingCompany: shippingCompany?.id ? { id: toNumber(shippingCompany.id) } : null,
          isEnabled,
          status: isEnabled
        };

        return {
          url: `/truck/save`,
          method: "POST",
          body
        };
      },
      onQueryStarted: async ({ id }, { queryFulfilled }) => {
        const isEdit = Boolean(id);
        toast.promise(queryFulfilled, {
          loading: isEdit ? "Atualizando caminhão..." : "Criando caminhão...",
          success: isEdit ? "Caminhão atualizado com sucesso!" : "Caminhão criado com sucesso!"
        });

        try {
          await queryFulfilled;
        } catch (error: unknown) {
          const err = error as { error: { data: { status: number; response: unknown; message: string } } };

          if (err.error.data.status === 502) {
            toast.error("A placa informada já está cadastrada e não pode ser utilizada.");
            return;
          }

          if (isEdit) {
            toast.error("Erro ao atualizar caminhão.");
            return;
          }

          toast.error("Erro ao criar caminhão.", { id: "create-truck-error" });
        }
      }
    }),
    /**
     * Delete a truck
     * @param id - Truck ID to delete
     * @returns void
     */
    deleteTruck: build.mutation<void, Pick<Truck, "id">>({
      query: ({ id }) => ({
        url: `/truck/delete/${id}`,
        method: "DELETE"
      }),
      onQueryStarted: async (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Excluindo caminhão...",
          success: "Caminhão excluído com sucesso!",
          error: "Erro ao excluir caminhão."
        });
      }
    }),

    /**
     * Rebind a truck to a different driver/company
     * @param truck - Truck rebind data
     * @returns Updated truck
     */
    rebindTruck: build.mutation<Truck, RebindTruckArgs>({
      query: ({ truck: { renavam, shippingCompany, ...body } }) => {
        return {
          url: "/truck/rebind",
          method: "PATCH",
          body: {
            truck: {
              ...body,
              renavam: renavam.replace(/\D/g, ""),
              shippingCompany: shippingCompany?.id ? { id: shippingCompany.id } : undefined
            }
          }
        };
      },
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Reatribuindo caminhão...",
          success: "Caminhão reatribuído com sucesso!",
          error: (err) => {
            const error = err;
            return error.error?.data?.message || "Erro ao reatribuir caminhão.";
          }
        });
      }
    }),

    /**
     * Fetches truck shipping filters (companies and orders).
     *
     * This endpoint queries for the list of companies and orders to be used as filters
     * in truck-related filtering (e.g., on a list/search screen). It first returns empty/default
     * filter lists, then asynchronously fetches the actual data and updates the query cache.
     *
     * - companies: List of shipping companies for use as filter options.
     * - orders: List of orders for use as filter options.
     *
     * @returns All required filters for trucks search
     */
    getTruckShippingFilters: build.query<TrucksFilters, void>({
      queryFn: () => ({
        data: {
          companies: [],
          customers: [],
          orders: [],
          drivers: []
        }
      }),
      onQueryStarted: async (args, { dispatch }) => {
        const companiesPromise = dispatch(companyApi.endpoints.getCompanies.initiate(undefined, { forceRefetch: true }));
        const ordersPromise = dispatch(orderApi.endpoints.getOrders.initiate(undefined, { forceRefetch: true }));
        const promises = [companiesPromise, ordersPromise] as const;

        const promiseAll = Promise.all(promises);
        toast.promise(promiseAll, {
          loading: "Carregando filtros...",
          success: "Filtros carregados com sucesso!",
          error: "Erro ao carregar filtros."
        });
        const [companiesResult, ordersResult] = await promiseAll;

        dispatch(
          truckApi.util.updateQueryData("getTruckShippingFilters", undefined, (draft) => {
            Object.assign<object, typeof draft>(draft, {
              companies: companiesResult.data ?? [],
              orders: ordersResult.data ?? []
            });
          })
        );
      }
    })
  })
});

/**
 * Enhance endpoints with cache tag configuration
 */
truckApi.enhanceEndpoints({
  addTagTypes: ["Truck", "TruckSearch", "TruckIsTruck", "TruckList", "TruckFilters"],
  endpoints: {
    getTrucks: {
      providesTags: (result) => (result ? [...result.map(({ id }) => ({ type: "Truck" as const, id })), { type: "Truck", id: "LIST" }] : [{ type: "Truck", id: "LIST" }])
    },
    getTruck: {
      providesTags: (result, error, { id }) => [{ type: "Truck", id }]
    },
    getIsTruck: {
      providesTags: (result, error, { licensePlate }) => [{ type: "TruckIsTruck", id: licensePlate }]
    },
    searchTrucks: {
      providesTags: (result) =>
        result ? [...result.map(({ id }) => ({ type: "TruckSearch" as const, id })), { type: "TruckSearch", id: "LIST" }] : [{ type: "TruckSearch", id: "LIST" }]
    },
    listTrucks: {
      providesTags: (result) =>
        result
          ? [...result.pages.flatMap((page) => page.data.map(({ id }) => ({ type: "TruckList" as const, id }))), { type: "TruckList", id: "LIST" }]
          : [{ type: "TruckList", id: "LIST" }]
    },
    createTruck: {
      invalidatesTags: (result, error, { id }) =>
        !error ? (id ? [{ type: "Truck", id: Number(id) }, "TruckList"] : [{ type: "Truck", id: "LIST" }, { type: "TruckSearch", id: "LIST" }, "TruckList"]) : []
    },
    saveNewTruck: {
      invalidatesTags: (result, error, { id }) =>
        !error ? (id ? [{ type: "Truck", id: Number(id) }, "TruckList", "Driver"] : [{ type: "Truck", id: "LIST" }, { type: "TruckSearch", id: "LIST" }, "TruckList"]) : []
    },
    deleteTruck: {
      invalidatesTags: (result, error, { id }) => (!error ? [{ type: "Truck", id }, { type: "Truck", id: "LIST" }, { type: "TruckSearch", id: "LIST" }, "TruckList"] : [])
    },
    rebindTruck: {
      invalidatesTags: (result, error, { truck }) =>
        !error && result && truck.driver?.id ? [{ type: "Truck", id: result.id }, { type: "Truck", id: "LIST" }, "TruckList"] : [{ type: "Truck", id: "LIST" }, "TruckList"]
    },
    getTruckShippingFilters: {
      providesTags: [{ type: "TruckFilters", id: "LIST" }]
    }
  }
});
